import React, { useState, useRef } from 'react';
import { utilsAPI } from '../../services/api';

const UtilsPanel = ({ onClose }) => {
  const [uploadResponse, setUploadResponse] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setError(null);

      const response = await utilsAPI.exportRawEnglish();

      // Create a download link for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Extract filename from content-disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'raw_english_export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      setError(`Export failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleUpload = async (file) => {
    try {
      setIsUploading(true);
      setError(null);
      setUploadResponse(null);

      // Validate file type
      if (!file.name.endsWith('.csv')) {
        setError('Please upload a CSV file');
        return;
      }

      const response = await utilsAPI.uploadRawEnglish(file);
      setUploadResponse(response.data);

    } catch (err) {
      setError(`Upload failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsUploading(false);
    }
  };

  const handleImport = async () => {
    try {
      setIsImporting(true);
      setError(null);

      const response = await utilsAPI.importRawEnglish();

      // Update upload response to show import result
      setUploadResponse({
        ...uploadResponse,
        importResult: response.data
      });

      alert('Import completed successfully!');

    } catch (err) {
      setError(`Import failed: ${err.response?.data?.message || err.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const renderObjectAsKeyValue = (obj, indent = 0) => {
    if (!obj || typeof obj !== 'object') {
      return <span>{String(obj)}</span>;
    }

    return (
      <div style={{ marginLeft: indent > 0 ? '20px' : '0' }}>
        {Object.entries(obj).map(([key, value]) => (
          <div key={key} style={{ marginBottom: '8px', padding: '4px 0' }}>
            <strong style={{ color: '#2d5016' }}>{key}:</strong>{' '}
            {typeof value === 'object' && value !== null ? (
              renderObjectAsKeyValue(value, indent + 1)
            ) : (
              <span>{String(value)}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '30px',
        maxWidth: '800px',
        width: '90%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '25px'
        }}>
          <h2 style={{ margin: 0, color: '#2d5016' }}>🛠️ Data Utilities</h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            padding: '12px',
            backgroundColor: '#ffebee',
            color: '#d32f2f',
            borderRadius: '4px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {/* Export Section */}
        <div style={{
          marginBottom: '25px',
          padding: '20px',
          border: '1px solid #e0e0e0',
          borderRadius: '6px'
        }}>
          <h3 style={{ marginTop: 0, color: '#2d5016' }}>📥 Export Data</h3>
          <p style={{ color: '#666', marginBottom: '15px' }}>
            Download current crop data as CSV file
          </p>
          <button
            onClick={handleExport}
            disabled={isExporting}
            style={{
              padding: '10px 20px',
              backgroundColor: isExporting ? '#ccc' : '#2d5016',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isExporting ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {isExporting ? 'Exporting...' : '📥 Export CSV'}
          </button>
        </div>

        {/* Upload Section */}
        <div style={{
          marginBottom: '25px',
          padding: '20px',
          border: '1px solid #e0e0e0',
          borderRadius: '6px'
        }}>
          <h3 style={{ marginTop: 0, color: '#2d5016' }}>📤 Upload Data</h3>
          <p style={{ color: '#666', marginBottom: '15px' }}>
            Upload a CSV file to stage for import
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            style={{
              padding: '10px 20px',
              backgroundColor: isUploading ? '#ccc' : '#4caf50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: isUploading ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {isUploading ? 'Uploading...' : '📤 Upload CSV'}
          </button>
        </div>

        {/* Upload Response */}
        {uploadResponse && (
          <div style={{
            padding: '20px',
            border: '1px solid #e0e0e0',
            borderRadius: '6px',
            backgroundColor: '#f8fdf8'
          }}>
            <h3 style={{ marginTop: 0, color: '#2d5016' }}>📊 Upload Response</h3>
            <div style={{
              fontSize: '14px',
              lineHeight: '1.6',
              marginBottom: '15px'
            }}>
              {renderObjectAsKeyValue(uploadResponse)}
            </div>

            {/* Import Button */}
            {uploadResponse.success && !uploadResponse.importResult && (
              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                  Data is ready to import. Click the button below to import to the main database.
                </p>
                <button
                  onClick={handleImport}
                  disabled={isImporting}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: isImporting ? '#ccc' : '#ff9800',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: isImporting ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {isImporting ? 'Importing...' : '⚡ Import to Database'}
                </button>
              </div>
            )}

            {/* Import Result */}
            {uploadResponse.importResult && (
              <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                <h4 style={{ color: '#2d5016' }}>✅ Import Result</h4>
                <div style={{ fontSize: '14px', lineHeight: '1.6' }}>
                  {renderObjectAsKeyValue(uploadResponse.importResult)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default UtilsPanel;
